import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Award } from "lucide-react";
import { CARRIERS } from "../lib/data";

const CarrierCard = ({ carrier, index }) => {
  // Use Google's favicon service for logo proxy
  const logo = `https://www.google.com/s2/favicons?domain=${carrier.domain}&sz=64`;

  return (
    <motion.a
      href={carrier.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`carrier-link-${carrier.name.replace(/\s+/g, "-").toLowerCase()}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.07 }}
      className="group relative gradient-border bg-white p-6 hover:bg-ivory transition-all duration-500 flex flex-col justify-between min-h-[200px] gold-glow"
    >
      <div className="relative flex items-start justify-between gap-3 mb-4">
        <div className="w-12 h-12 bg-ivory border border-line group-hover:border-gold flex items-center justify-center overflow-hidden transition-colors">
          <img
            src={logo}
            alt={`${carrier.name} logo`}
            className="w-7 h-7 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <ExternalLink className="w-4 h-4 text-ink/30 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500" />
      </div>

      <div className="relative">
        <h3 className="font-display font-light text-lg sm:text-xl text-ink leading-tight group-hover:text-gold transition-colors duration-500">
          {carrier.name}
        </h3>
        <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-ink/40 font-semibold">
          {carrier.region}
        </p>
      </div>

      {/* BBB badge */}
      <div className="relative mt-4 pt-4 border-t border-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Award className="w-3.5 h-3.5 text-gold" />
          <span className="text-[10px] eyebrow text-ink/60">BBB</span>
        </div>
        <span className="font-display text-base text-gold font-medium">{carrier.bbb}</span>
      </div>

      <div className="absolute bottom-0 left-0 h-px w-0 bg-gold group-hover:w-full transition-all duration-700" />
    </motion.a>
  );
};

const Carriers = () => {
  // Duplicate for infinite ticker
  const tickerList = [...CARRIERS, ...CARRIERS];
  return (
    <section
      id="carriers"
      data-testid="carriers-section"
      className="relative bg-gradient-to-b from-cream/30 via-ivory to-cream/40 py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grid-overlay opacity-25" />
      <div className="absolute top-1/4 right-0 w-72 h-72 bg-gold/8 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-16 sm:mb-20"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-gold" />
              <span className="eyebrow text-gold">Carriers We Represent</span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ink">
              Twelve trusted <span className="italic gold-text-gradient">partners.</span>
            </h2>
          </div>
          <p className="text-ink/65 text-base sm:text-lg leading-relaxed font-light max-w-md">
            As an independent broker, I shop across the nation's top-rated carriers to build the
            policy that fits <span className="text-ink font-medium">your</span> family — not theirs.
            Every partner below is verified through the Better Business Bureau. Click any carrier to
            explore them directly.
          </p>
        </motion.div>

        {/* Ticker strip */}
        <div className="relative overflow-hidden mb-12 py-3 border-y border-line bg-ivory/40">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-ivory to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-ivory to-transparent z-10 pointer-events-none" />
          <div className="flex gap-12 ticker-track whitespace-nowrap">
            {tickerList.map((c, i) => (
              <div key={`${c.domain}-${i}`} className="flex items-center gap-3 text-ink/55">
                <img
                  src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=64`}
                  alt=""
                  className="w-5 h-5 object-contain opacity-70"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                <span className="font-display text-base">{c.name}</span>
                <span className="text-gold text-xs eyebrow">· BBB {c.bbb}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {CARRIERS.map((c, i) => (<CarrierCard key={c.name} carrier={c} index={i} />))}
        </div>

        {/* BBB note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-10 text-center text-xs text-ink/50 font-light max-w-3xl mx-auto"
        >
          Better Business Bureau (BBB) ratings reflect the bureau's evaluation of each carrier's
          customer trust, complaint history, and business practices. Ratings are publicly verifiable
          at <span className="text-gold">bbb.org</span>.
        </motion.p>
      </div>
    </section>
  );
};

export default Carriers;
